import { useEffect, useMemo, useState } from 'react';
import { bossDisplayName } from '../../../shared/domain/bossPresentation.js';
import { formatRaidHpEok } from '../../../shared/domain/raidHpFormat.js';
import { RefreshIconButton } from '../../../shared/ui/RefreshIconButton.jsx';
import { ElementIcon } from '../../nikke-catalog/shared/ElementIcon.jsx';
import { useRaidOverview } from '../hooks/useRaidOverview.js';
import { RaidAttackStatus } from '../shared/RaidAttackStatus.jsx';
import { RaidBossDetailModal } from '../shared/RaidBossDetailModal.jsx';
import { siteImageUrl } from '../../../shared/domain/siteImages.js';

function formatEok(value, digits = 2) {
  if (value === null || value === undefined) return '인피니티';
  return `${Number(value || 0).toLocaleString('ko-KR', { maximumFractionDigits: digits })}억`;
}

function formatInfinityEok(value) {
  return `${Number(value || 0).toLocaleString('ko-KR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}억`;
}

function formatPercent(value) {
  return `${Number(value || 0).toLocaleString('ko-KR', { maximumFractionDigits: 1 })}%`;
}

function Kpi({ label, value, sub }) {
  return <article className="raid-mobile-kpi"><span>{label}</span><strong>{value}</strong><small>{sub}</small></article>;
}

function MobileInfinityBossInfo({ boss }) {
  return (
    <div className="raid-mobile-boss-info raid-mobile-infinity-info">
      <div className="raid-mobile-boss-title">
        <strong>{bossDisplayName(boss)}</strong>
        <span className="raid-mobile-boss-element" aria-label={boss.element || '속성'} title={boss.element || '속성'}>
          <ElementIcon element={boss.element} />
        </span>
      </div>
      <div className="raid-mobile-infinity-meta">
        <span>4단</span><b><i aria-hidden="true">∞</i> 인피니티</b>
      </div>
      <small className="raid-mobile-infinity-label">누적 딜</small>
      <strong className="raid-mobile-infinity-damage">{formatInfinityEok(boss.currentDamageEok)}</strong>
      <div className="raid-mobile-hp-track infinite raid-mobile-infinity-track" aria-label="인피니티 체력"><span /></div>
      <div className="raid-mobile-infinity-note"><b aria-hidden="true">∞</b><div><strong>인피니티 보스</strong><small>전체 단수 평균 {formatEok(boss.averageDamageEok)} · 공격 제한 없음</small></div></div>
    </div>
  );
}

function MobileStandardBossInfo({ boss }) {
  return (
    <div className="raid-mobile-boss-info">
      <div className="raid-mobile-boss-title">
        <strong>{bossDisplayName(boss)}</strong>
        <span className="raid-mobile-boss-element" aria-label={boss.element || '속성'} title={boss.element || '속성'}>
          <ElementIcon element={boss.element} />
        </span>
      </div>
      <div className="raid-mobile-boss-meta"><span>{boss.currentPhase}단</span><span>{boss.attackCount}타</span><span>평균 {formatEok(boss.averageDamageEok)}</span></div>
      <div className="raid-mobile-hp-track"><span style={{ width: `${boss.remainingPercent}%` }} /></div>
      <small>{formatRaidHpEok(boss.remainingHpEok)} / {formatRaidHpEok(boss.maxHpEok)}</small>
    </div>
  );
}

function BossCard({ boss, onOpen }) {
  return (
    <button className={`raid-mobile-boss-card ${boss.isCleared ? 'cleared' : ''} ${boss.isInfinite ? 'infinite' : ''}`} type="button" onClick={onOpen}>
      <div className="raid-mobile-boss-image" style={{ backgroundImage: boss.imageUrl ? `url(${siteImageUrl(boss.imageUrl)})` : undefined }}>
        {boss.isCleared ? <strong>CLEAR</strong> : null}
      </div>
      {boss.isInfinite ? <MobileInfinityBossInfo boss={boss} /> : <MobileStandardBossInfo boss={boss} />}
    </button>
  );
}

function MobileInfinityFeature({ boss, onOpen }) {
  const backgroundImage = boss.imageUrl
    ? `linear-gradient(180deg,rgba(5,10,15,.26),rgba(5,12,17,.94)),url(${siteImageUrl(boss.imageUrl)})`
    : undefined;
  return (
    <button className="raid-home-infinity-feature raid-mobile-infinity-feature" type="button" style={{ backgroundImage }} onClick={onOpen}>
      <div className="raid-home-infinity-feature-copy">
        <span>INFINITY BOSS</span>
        <h3>{bossDisplayName(boss)}</h3>
        <div className="raid-home-infinity-feature-weak"><b>WEAK POINT</b><ElementIcon element={boss.element} /></div>
        <p>3단 전체 클리어 · 5보스 인피니티 진행</p>
      </div>
      <div className="raid-home-infinity-feature-stats">
        <div><span>현재 단계</span><strong>4단</strong></div>
        <div><span>누적 딜</span><strong>{formatInfinityEok(boss.currentDamageEok)}</strong></div>
      </div>
      <div className="raid-home-infinity-feature-mark"><span aria-hidden="true">∞</span><strong>인피니티</strong><small>공격 제한 없음</small></div>
    </button>
  );
}

export function MobileRaidOverview({ canManage = false, onNavigate = () => {} }) {
  const raid = useRaidOverview();
  const season = raid.selectedSeason;
  const [detailBossNo, setDetailBossNo] = useState(null);
  const [showAllBosses, setShowAllBosses] = useState(false);
  const selectedBoss = useMemo(
    () => season?.bosses?.find((boss) => boss.bossNo === detailBossNo) || null,
    [detailBossNo, season?.bosses],
  );

  useEffect(() => {
    setShowAllBosses(false);
  }, [season?.globalPhase, season?.seasonNo]);

  if (raid.loading) return <section className="raid-mobile-state"><span /><p>실전 현황 계산 중</p></section>;
  if (raid.errorMessage) return <section className="raid-mobile-state error"><strong>현황을 불러오지 못했습니다.</strong><p>{raid.errorMessage}</p></section>;
  if (!season) return <section className="raid-mobile-state"><strong>표시할 차수 없음</strong><p>레이드 차수를 먼저 저장해주세요.</p></section>;

  const infinityBoss = season.bosses.find((boss) => Number(boss.bossNo) === 5);
  const isInfinityStage = season.globalPhase === 4 && Boolean(infinityBoss);

  return (
    <section className="raid-mobile-home" aria-label="유니온 레이드 현황">
      <section className="raid-mobile-hero-card">
        <div className="raid-mobile-hero-main">
          <div><span>UNION RAID</span><h1>{season.globalPhase}단 진행중</h1><p>남은 {season.remainingAttacks}타 · 실전 {season.recordCount}/{season.totalAttackLimit}타</p></div>
          <div className="raid-mobile-season-controls">
            <label className="raid-mobile-season-select"><select aria-label="조회 차수" value={season.seasonNo} onChange={(event) => raid.selectSeason(event.target.value)}>{raid.seasons.map((item) => <option key={item.seasonNo} value={item.seasonNo}>{item.seasonNo}차</option>)}</select></label>
            <RefreshIconButton onClick={() => raid.refresh(season.seasonNo)} />
          </div>
        </div>
        <div className="raid-mobile-progress-head"><span>진행도</span><b>{formatPercent(season.stageProgressPercent)}</b></div>
        <div className="raid-mobile-progress"><span style={{ width: `${season.stageProgressPercent}%` }} /></div>
        <small>{isInfinityStage ? `${bossDisplayName(infinityBoss, 5)} 인피니티 진행` : `남은 HP ${formatRaidHpEok(season.currentStageRemainingHpEok)} / ${formatRaidHpEok(season.currentStageTotalHpEok)}`}</small>
        {canManage ? <div className="raid-mobile-hero-actions"><button type="button" onClick={() => onNavigate('tetris')}>테트리스</button><button type="button" onClick={() => onNavigate('management/season')}>보스 설정</button></div> : null}
      </section>

      <section className="raid-mobile-kpi-grid">
        <Kpi label="멤버" value={`${season.memberCount}명`} sub="차수 기준" />
        <Kpi label="남은 타수" value={`${season.remainingAttacks}타`} sub={`총 ${season.totalAttackLimit}타`} />
        <Kpi label="실전 기록" value={season.recordCount} sub="하드 기준" />
        <Kpi label="남은 HP" value={isInfinityStage ? '∞' : formatRaidHpEok(season.currentStageRemainingHpEok)} sub={`${formatPercent(season.stageProgressPercent)} 진행`} />
      </section>

      <section className="raid-mobile-section">
        <header>
          <h2>보스 현황</h2>
          {isInfinityStage ? <button className="raid-mobile-boss-view-toggle" type="button" aria-pressed={showAllBosses} onClick={() => setShowAllBosses((current) => !current)}>{showAllBosses ? '5보스만 보기' : '전체 보스 보기'}</button> : <span>탭하면 상세</span>}
        </header>
        {isInfinityStage && !showAllBosses
          ? <MobileInfinityFeature boss={infinityBoss} onOpen={() => setDetailBossNo(infinityBoss.bossNo)} />
          : <div className="raid-mobile-boss-stack">{season.bosses.map((boss) => <BossCard boss={boss} key={boss.bossNo} onOpen={() => setDetailBossNo(boss.bossNo)} />)}</div>}
      </section>

      <section className="raid-mobile-section">
        <header><h2>상위 딜러</h2><span>TOP 3</span></header>
        <div className="raid-mobile-rank-list">
          {season.topDealers.length ? season.topDealers.slice(0, 3).map((member, index) => <div key={member.openid}><span>{index + 1}</span><strong>{member.nickname}</strong><em>{formatEok(member.totalDamageEok)}</em></div>) : <p>실전 기록 없음</p>}
        </div>
      </section>

      <section className="raid-mobile-section">
        <header><h2>공격 상태</h2><span>3타 기준</span></header>
        <RaidAttackStatus groups={season.attackGroups} />
      </section>

      {selectedBoss ? <RaidBossDetailModal boss={selectedBoss} records={season.records} onClose={() => setDetailBossNo(null)} /> : null}
    </section>
  );
}
